import { remove } from 'lodash';
import { useAiAlertStore } from '@src/globalStore/aiAlertStore';
import { metricKeys } from '@pulse/metrics';
import { useSelector } from 'react-redux';
import {
  aiAlertsCustomMetricsLimit,
  aiAlertsPCPFeatureSelector,
  appAIAlertFeatureFlagSelector,
  featureSelector,
} from '@src/store/common/selectors';
import { CURRENT_PRODUCT_MODE, isAdModule, isAppModule, isEiModule } from '@src/utils/constants';
import metricsStore from '@src/globalStore/metricsStore';
import { useFetchSensitivity } from '@src/components/ai-alerts/hooks/useSensitivity';
import {
  AI_ALERT_KPI_BETA_METRICS,
  AI_ALERT_STANDARD_METRICS_MAP,
  MINS_WITH_X_METRICS_WITH_FF,
} from '@src/components/ai-alerts/constants';
import { useEffect } from 'react';
import { createMetricConfigForMetrics } from '../utils';
import { getFeature } from '@src/globalStore/featureStore';
import featureNames, { globalFeatureNames } from '@src/utils/featureToggle/constants';
import { useGlobalFeature } from '@globalStore/globalFeatureFlagsStore';

const useAIAlertMetricsMeta = (
  enabled,
  sensitivityFormData: any = [],
  options: { excludeConcurrentPlays?: boolean } = {},
) => {
  const {
    data: sensitivityResponseData = [],
    kpiId,
    isFetching,
    isError,
    refetch,
  } = useFetchSensitivity(enabled);
  const finalSensitivityData = sensitivityFormData.length
    ? sensitivityFormData
    : sensitivityResponseData;
  const { preDefinedMetricsMeta, userDefinedMetricsMeta, flowConfigMeta } = metricsStore.getState();
  const customMetrics = userDefinedMetricsMeta[CURRENT_PRODUCT_MODE.toLowerCase()];
  const standardMetrics = preDefinedMetricsMeta[CURRENT_PRODUCT_MODE.toLowerCase()];
  const { setAIAlertMetricsMeta } = useAiAlertStore((state) => ({
    setAIAlertMetricsMeta: state.setAIAlertMetricsMeta,
  }));

  const isAppAlertEnabled = useSelector(appAIAlertFeatureFlagSelector);
  const isPcpEnabled = useSelector(aiAlertsPCPFeatureSelector);
  const isPcpFromNewSystem = useGlobalFeature(globalFeatureNames.IS_PCP_FROM_NEW_SYSTEM);
  const excludeConcurrentPlays = options.excludeConcurrentPlays ?? !isPcpFromNewSystem;
  const isAlertPartnerAccount = useSelector(featureSelector('is_alert_forwarding_partner'));
  const isContentAlertsEnabled = useSelector(featureSelector('ai_alerts')) || isAlertPartnerAccount;
  const isAdAlertEnabled = useSelector(featureSelector('ad_ai_alerts'));
  const aiAlertCustomMetricLimit: any = useSelector(aiAlertsCustomMetricsLimit);

  // filter out 0/4xx/5xx metrics based on feature flag
  // TODO: remove this when the feature is GA
  const minWithXMetricsEnabled = getFeature(featureNames.ECO_AI_ALERT_MINS_WITH_X_METRICS);
  const filteredSensitivityData = finalSensitivityData.filter(
    ({ metricKey }) => !MINS_WITH_X_METRICS_WITH_FF.includes(metricKey) || minWithXMetricsEnabled,
  );

  const aiAlertMetricKeys: string[] = [],
    aiAlertMetricsMeta: any[] = [];

  // for email subscription in ad, ad metrics meta is needed
  if (isAdModule && isAdAlertEnabled) {
    const adMetrics: string[] = [];
    AI_ALERT_STANDARD_METRICS_MAP.AI.forEach((metric) => {
      const adMetric = metric.replace('Ad', '');
      if (adMetric in standardMetrics) {
        adMetrics.push(adMetric);
      }
    });
    aiAlertMetricKeys.push(...adMetrics);
    aiAlertMetricsMeta.push(...createMetricConfigForMetrics(adMetrics));
  }

  if (isEiModule) {
    // adding ad metrics in video module for sensitivity and ai alerts summary and diagnostics
    if (isAdAlertEnabled) {
      const adMetrics = AI_ALERT_STANDARD_METRICS_MAP.AI.filter(
        (metric) => metric in standardMetrics,
      );
      aiAlertMetricKeys.push(...adMetrics);
      aiAlertMetricsMeta.push(...createMetricConfigForMetrics(adMetrics));
    }

    if (isContentAlertsEnabled) {
      const eiMetrics = AI_ALERT_STANDARD_METRICS_MAP.EI.filter(
        (metric) => metric in standardMetrics,
      );
      aiAlertMetricKeys.push(...eiMetrics);
      aiAlertMetricsMeta.push(...createMetricConfigForMetrics(eiMetrics));
      if (!isPcpEnabled || excludeConcurrentPlays) {
        remove(aiAlertMetricKeys, (item) => item === metricKeys.ConcurrentPlays);
        remove(aiAlertMetricsMeta, { name: metricKeys.ConcurrentPlays });
      }

      // Beta KPI metrics: surfaced in the metric list so they render as opt-in checkbox
      // rows, but not auto-added to the saved config (handled in the row/save flow).
      const betaMetrics = AI_ALERT_KPI_BETA_METRICS.filter((metric) => metric in standardMetrics);
      aiAlertMetricKeys.push(...betaMetrics);
      aiAlertMetricsMeta.push(
        ...createMetricConfigForMetrics(betaMetrics).map((meta) => ({ ...meta, isBeta: true })),
      );
    }
  }

  if (isAppModule && isAppAlertEnabled) {
    const appAIAlertOOBMetrics = minWithXMetricsEnabled
      ? AI_ALERT_STANDARD_METRICS_MAP.APP
      : AI_ALERT_STANDARD_METRICS_MAP.APP.filter(
          (metric) => !MINS_WITH_X_METRICS_WITH_FF.includes(metric),
        );
    const appMetrics = appAIAlertOOBMetrics.filter((metric) => metric in standardMetrics);
    aiAlertMetricKeys.push(...appMetrics);
    aiAlertMetricsMeta.push(...createMetricConfigForMetrics(appMetrics));

    const appCustomMetricList: string[] = (filteredSensitivityData || [])
      .filter(({ metricKey }) => !AI_ALERT_STANDARD_METRICS_MAP.APP.includes(metricKey))
      .map(({ metricKey }) => metricKey);

    if (aiAlertCustomMetricLimit > 0) {
      appCustomMetricList.forEach((metric) => {
        const metricDefinition = customMetrics[metric];

        if (metricDefinition && metricDefinition.flowID) {
          const flowDefinition = flowConfigMeta[metricDefinition.flowID];

          if (flowDefinition) {
            const flowCategory = flowConfigMeta[metricDefinition.flowID].name;

            aiAlertMetricKeys.push(metric);
            aiAlertMetricsMeta.push({
              ...createMetricConfigForMetrics([metric], true, true)[0],
              flowCategory,
            });
          }
        } else if (metricDefinition && metricDefinition.flowID === null) {
          aiAlertMetricKeys.push(metric);
          aiAlertMetricsMeta.push(createMetricConfigForMetrics([metric], true, false)[0]);
        }
      });
    }
  }

  useEffect(() => {
    setAIAlertMetricsMeta(aiAlertMetricsMeta);
  }, []);

  return {
    aiAlertMetricKeys,
    kpiId,
    isFetching,
    isError,
    refetch,
    sensitivityResponseData: sensitivityResponseData || [],
    sensitivityFormData: filteredSensitivityData,
    aiAlertMetricsMeta,
  };
};

export default useAIAlertMetricsMeta;
